import { NegotiationObjective } from '@procureai/constellation/clara/negotiation-case/v1.avsc';
import {
  NegotiationBoundaryBase,
  NegotiationInitiationMode,
  NegotiationMode,
  NegotiationSupplierInteractionMode,
  QuoteMode,
} from '@procureai/schemas';
import {
  BiddingAndNegotiationSettings,
  UseEditableWidgetBackend,
  biddingAndNegotiationSettingsSchema,
} from '@procureai/ui-modules-mui';

import {
  TsrctInitiationMode,
  TsrctNegotiationCaseStatus,
  TsrctNegotiationMode,
  TsrctNegotiationObjective,
  TsrctNegotiationStrategyBoundaryBase,
  TsrctQuoteMode,
  TsrctSupplierInteractionMode,
} from '@/generated/gql/graphql';
import { createEditableWidgetGraphqlBackendHook } from '@/shared/hooks/useEditableWidgetGraphqlBackend/useEditableWidgetGraphqlBackend';
import { useModuleContext } from '~/components/ModuleContextProvider';

import { NEGOTIATION_SETTINGS_MUTATION } from './NegotiationSettingsWidget.mutation.gql.ts';
import { QUOTE_SOURCING_CASE_NEGOTIATION_SETTINGS_QUERY } from './NegotiationSettingsWidget.query.gql.ts';

const useNegotiationSettingsWidgetBackendInternal = createEditableWidgetGraphqlBackendHook({
  schema: biddingAndNegotiationSettingsSchema,
  query: QUOTE_SOURCING_CASE_NEGOTIATION_SETTINGS_QUERY,
  mutation: NEGOTIATION_SETTINGS_MUTATION,
});

export const useNegotiationSettingsWidgetBackend: UseEditableWidgetBackend<BiddingAndNegotiationSettings> = ({
  i18n,
}) => {
  const { entityId } = useModuleContext();
  const variables = { id: entityId };

  return useNegotiationSettingsWidgetBackendInternal({
    i18n,
    variables,
    skip: !entityId,
    toFormData: (data) => {
      const sourcingCase = data?.tesseract.negotiationCase;
      const options = sourcingCase?.options;
      const strategy = sourcingCase?.strategy;
      return {
        negotiationEnabled: sourcingCase.isNegotiationEnabled ?? false,
        supplierUploadEnabled: sourcingCase.isSupplierUploadAllowed ?? false,
        initiatedBy: options?.initiatedBy ?? NegotiationInitiationMode.PROCUREAI,
        strategy: options?.negotiationObjective as unknown as NegotiationObjective,
        supplierInteractionMode: options?.supplierInteractionMode as unknown as NegotiationSupplierInteractionMode,
        negotiationMode: options?.negotiationMode as unknown as NegotiationMode,
        quoteMode: options?.quoteMode as unknown as QuoteMode,
        boundaries: strategy?.boundaryRules?.map((b) => ({
          base: b.base as NegotiationBoundaryBase,
          lower: Number(b.lower),
          upper: Number(b.upper),
        })) ?? [
          {
            base: NegotiationBoundaryBase.QUOTED_PRICE,
            lower: undefined,
            upper: undefined,
          },
        ],
        boundaryAdjustments: {
          historicNegotiations: strategy?.useHistoricNegotiationData ?? false,
          historicTenderData: strategy?.useHistoricTenderData ?? false,
        },
      };
    },
    toMutationData: (formData) => {
      return {
        id: entityId,
        data: {
          isEnabled: formData.negotiationEnabled ?? false,
          isSupplierUploadAllowed: formData.supplierUploadEnabled ?? false,
          negotiationOptions: {
            initiatedBy: formData.initiatedBy as unknown as TsrctInitiationMode,
            negotiationMode: formData.negotiationMode as unknown as TsrctNegotiationMode,
            negotiationObjective: formData.strategy as unknown as TsrctNegotiationObjective,
            quoteMode: formData.quoteMode as unknown as TsrctQuoteMode,
            supplierInteractionMode: formData.supplierInteractionMode as unknown as TsrctSupplierInteractionMode,
          },
          negotiationStrategy: {
            boundaryAdjustments: formData.boundaryAdjustments,
            boundaryRules: formData.boundaries.map((b) => ({
              base: b.base as TsrctNegotiationStrategyBoundaryBase,
              lower: b.lower.toString(),
              upper: b.upper.toString(),
            })),
          },
        },
      };
    },
    toSimpleFormWidgetProps: (data, loading) => {
      return loading || data?.tesseract.negotiationCase.status !== TsrctNegotiationCaseStatus.INITIALIZED
        ? { disabled: true, loading }
        : { disabled: false, loading };
    },
  });
};
